// Apple Health integration via the capacitor-health plugin. All imports are
// dynamic with graceful web fallback — on web every function returns null /
// false and the UI shows "available on iPhone".
//
// capacitor-health only exposes a fixed set of iOS reads: aggregated steps,
// active calories and mindfulness, plus workouts (which carry per-workout
// calories, distance and heart-rate samples). Daily resting HR, sleep, SpO2
// etc. are NOT surfaced by this plugin — we collect everything it does offer.
// The backend snapshot schema already accepts sleepMinutes/restingHeartRate
// for when a richer source lands.

import { isNative } from '../utils/platform';

// Everything the plugin can read on iOS. WRITE_* omitted — Cadence is read-only.
const PERMISSIONS = [
  'READ_STEPS',
  'READ_ACTIVE_CALORIES',
  'READ_TOTAL_CALORIES',
  'READ_DISTANCE',
  'READ_HEART_RATE',
  'READ_WORKOUTS',
  'READ_MINDFULNESS',
  // Added by patches/capacitor-health+8.1.2.patch (not upstream): sleep
  // stages, daily resting HR and HRV, plus 'sleep'/'resting-heart-rate'/
  // 'hrv'/'distance' aggregate queries to read them.
  'READ_SLEEP',
  'READ_RESTING_HEART_RATE',
  'READ_HRV',
];

// Race any promise against a timeout so a stalled native bridge can never hang
// a caller (the Health page, the briefing and chat all await this path).
function withTimeout(promise, ms, fallback) {
  return Promise.race([
    Promise.resolve(promise).catch(() => fallback),
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

// ⚠️ THENABLE-PROXY TRAP: a Capacitor plugin handle is a JS Proxy that
// intercepts EVERY property get — including `then` — so awaiting it (or
// returning it from an async function) hangs the caller forever: proxy.then
// swallows the resolve/reject callbacks in a bogus native call. This was the
// root cause of the Health "Connecting…" hang (getDailySummary's
// `await plugin()` never resumed after the permission grant). Async helpers
// here wrap the proxy in { api }; never return the bare proxy.

// Time-boxed module load. Throws on timeout/failure so callers can surface a
// distinct error. Resolves { api } — see thenable-proxy trap above.
async function importHealth() {
  const TIMED_OUT = Symbol('timeout');
  const mod = await Promise.race([
    import('capacitor-health'),
    new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), 6000)),
  ]);
  if (mod === TIMED_OUT) throw new Error('the Health plugin didn’t load in time');
  return { api: mod.Health };
}

// Resolves { api }, or null on web / when unavailable. The whole native path
// is time-boxed so an unregistered/stalled bridge resolves to null instead of
// hanging forever.
async function plugin() {
  if (!isNative()) return null;
  try {
    const { api } = await importHealth();
    const res = await withTimeout(api.isHealthAvailable(), 4000, null);
    return res?.available ? { api } : null;
  } catch {
    return null;
  }
}

export async function isHealthAvailable() {
  return (await plugin()) !== null;
}

// Requests read access to every metric. Returns a structured result instead of
// throwing so the UI can show the *actual* native error (crucial for
// diagnosing device-only failures we can't reproduce on the Simulator).
export async function requestHealthPermissions() {
  if (!isNative()) return { ok: false, error: 'not-native' };
  let health;
  try {
    ({ api: health } = await importHealth());
  } catch (err) {
    return { ok: false, error: `plugin-load-failed: ${err?.message || err}` };
  }
  try {
    // Don't gate on isHealthAvailable() here — on a real iPhone HealthKit is
    // always available, and a false-negative from the availability probe must
    // not block the permission sheet. Ask directly; the plugin no-ops on web.
    //
    // Backstop timeout: the native sheet requires user interaction so allow a
    // generous window, but never let the button spin forever if the bridge
    // wedges (the root-cause main-thread fix lives in the plugin's Swift).
    const TIMED_OUT = Symbol('timeout');
    const res = await Promise.race([
      health.requestHealthPermissions({ permissions: PERMISSIONS }),
      new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), 60000)),
    ]);
    if (res === TIMED_OUT) {
      return { ok: false, error: 'Apple Health didn’t respond. Open Settings › Health › Data Access & Devices › Cadence to grant access, then reopen Cadence.' };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
}

// Opens iOS Settings so the user can flip Health data access if they declined.
export async function openHealthSettings() {
  const health = (await plugin())?.api;
  if (!health) return;
  try { await health.openAppleHealthSettings(); } catch { /* best effort */ }
}

function dayRange(date) {
  const start = new Date(`${date}T00:00:00`);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

async function aggregate(health, request) {
  const res = await withTimeout(health.queryAggregated(request), 4000, null);
  if (!res) return null;
  return (res.aggregatedData || []).reduce((sum, b) => sum + (b.value || 0), 0);
}

// Daily summary for a YYYY-MM-DD date, shaped like the backend's
// /me/health/snapshot body. Missing metrics stay undefined so partial days
// (or metrics the user didn't grant) simply don't appear.
export async function getDailySummary(date) {
  const health = (await plugin())?.api;
  if (!health) return null;
  const { startDate, endDate } = dayRange(date);
  const summary = { date };

  try {
    const steps = await aggregate(health, { startDate, endDate, dataType: 'steps', bucket: 'day' });
    if (steps != null) summary.steps = Math.round(steps);
  } catch (err) { console.warn('Health steps query failed:', err); }

  try {
    const cals = await aggregate(health, { startDate, endDate, dataType: 'active-calories', bucket: 'day' });
    if (cals != null) summary.activeEnergyKcal = Math.round(cals);
  } catch (err) { console.warn('Health calories query failed:', err); }

  try {
    const mindful = await aggregate(health, { startDate, endDate, dataType: 'mindfulness', bucket: 'day' });
    if (mindful != null && mindful > 0) summary.mindfulnessMinutes = Math.round(mindful / 60);
  } catch (err) { console.warn('Health mindfulness query failed:', err); }

  // Sleep for the night ending on `date`, attributed to the wake-up morning
  // (noon-to-noon window). The patched plugin unions overlapping stage records
  // so iPhone + Watch double-tracking doesn't double-count, and returns the
  // first-asleep / last-woke timestamps alongside the total.
  try {
    const nightStart = new Date(`${date}T12:00:00`);
    nightStart.setDate(nightStart.getDate() - 1);
    const res = await withTimeout(health.queryAggregated({
      startDate: nightStart.toISOString(),
      endDate: new Date(`${date}T12:00:00`).toISOString(),
      dataType: 'sleep',
      bucket: 'day',
    }), 4000, null);
    const night = res?.aggregatedData?.[0];
    if (night?.value > 0) {
      summary.sleepMinutes = Math.round(night.value / 60);
      if (night.startDate) summary.sleepStart = new Date(night.startDate).toISOString();
      if (night.endDate) summary.sleepEnd = new Date(night.endDate).toISOString();
    }
  } catch (err) { console.warn('Health sleep query failed:', err); }

  try {
    const res = await withTimeout(health.queryAggregated({ startDate, endDate, dataType: 'resting-heart-rate', bucket: 'day' }), 4000, null);
    const rhr = (res?.aggregatedData || []).find((b) => b.value > 0)?.value;
    if (rhr) summary.restingHeartRate = Math.round(rhr);
  } catch (err) { console.warn('Health resting HR query failed:', err); }

  try {
    const res = await withTimeout(health.queryAggregated({ startDate, endDate, dataType: 'hrv', bucket: 'day' }), 4000, null);
    const hrv = (res?.aggregatedData || []).find((b) => b.value > 0)?.value;
    if (hrv) summary.hrvMs = Math.round(hrv);
  } catch (err) { console.warn('Health HRV query failed:', err); }

  try {
    const dist = await aggregate(health, { startDate, endDate, dataType: 'distance', bucket: 'day' });
    if (dist != null && dist > 0) summary.distanceMeters = Math.round(dist);
  } catch (err) { console.warn('Health distance query failed:', err); }

  try {
    const res = await withTimeout(
      health.queryWorkouts({ startDate, endDate, includeHeartRate: true, includeRoute: false, includeSteps: false }),
      4000,
      null,
    );
    const workouts = res?.workouts || [];
    if (workouts.length) {
      summary.workouts = workouts.map((w) => ({
        type: w.workoutType,
        minutes: Math.round((w.duration || 0) / 60),
        calories: Math.round(w.calories || 0),
        distanceMeters: w.distance ? Math.round(w.distance) : undefined,
      }));
      summary.exerciseMinutes = summary.workouts.reduce((sum, w) => sum + w.minutes, 0);
      // Workout distance is a fallback — the daily walking+running aggregate
      // above is the fuller number when present.
      const dist = workouts.reduce((sum, w) => sum + (w.distance || 0), 0);
      if (dist > 0 && summary.distanceMeters == null) summary.distanceMeters = Math.round(dist);
      const hrSamples = workouts.flatMap((w) => w.heartRate || []);
      if (hrSamples.length) {
        summary.heartRateAvg = Math.round(hrSamples.reduce((sum, s) => sum + s.bpm, 0) / hrSamples.length);
      }
    }
  } catch (err) { console.warn('Health workouts query failed:', err); }

  return summary;
}
